import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { config } from "../config";
import { OrganizationKeyName } from "../model/Organization";
import {
  AutoAddedProductMutationPayload,
  AutoAddedProductsResponse,
} from "./api-response/AutoAddedProducts";
import {
  ChallengeType,
  ChallengesResponse,
  DeleteChallengeType,
  UserChallengeApiResponse,
} from "./api-response/Challenges";
import {
  PunchCardResponse,
  PunchCardsResponse,
  PunchCardType,
} from "./api-response/PunchCards";
import { ArchiveVoucherTypeMutationType } from "../module/voucher-type/models/VoucherType";
import { extractErrorMessageAndErrorCode } from "../error-handler";
import * as queryString from "query-string";
import {
  DeliveryRuleForm,
  DeliveryRulesSuccessResponse,
} from "./api-response/DeliveryRule";
import { ImageUpload } from "./api-response/ImageUpload";
import { BaseUploadImage, UploadImage } from "./api-response/UploadImage";
import { UserTermListSuccessResponse } from "./api-response/UserTermListSuccessResponse";
import {
  CreateHomepage,
  HomepageConfigListSuccessResponse,
  UpdateHomepage,
} from "../module/shop/model/HomepageConfigListSuccessResponse";
import {
  CreateNews,
  NewsListSuccessResponse,
  NewsSuccessResponse,
  UpdateNews,
} from "../module/shop/model/NewsListSuccessResponse";
import { CountriesSuccessResponse } from "./api-response/CountriesSuccessResponse";
import {
  AppCallToActionsSuccessResponse,
  CreateCallToAction,
  UpdateCallToAction,
} from "./api-response/AppCallToAction";
import { UserOrdersSuccessResponse } from "../module/shop/model/OrderSuccessResponse";
import {
  AdditionGroupSuccesResponse,
  ShopProductAdditionsListSuccessResponse,
  AdditionGroupPayloadUpdate,
  AdditionGroupPayloadCreation,
  ArchiveAdditionGroup,
} from "../module/shop/model/ShopProductAdditionsListSuccessResponse";
import { UsersMemberCardsSuccessResponse } from "./api-response/UsersMemberCardsSuccessResponse";
import { UserMemberCardPosStatusSuccessResponse } from "./api-response/UserMemberCardPosStatusSuccessResponse";
import {
  UserMemberCardCreatePayload,
  UserMemberCardDeletePayload,
  UserMemberCardSyncCardSuccessResponse,
  UserMemberCardSyncPayload,
} from "./api-response/UserMemberCardSyncCardSuccessResponse";
import { ShopListSuccessResponse } from "../module/shop/model/ShopListSuccessResponse";
import { ShopSuccessResponse } from "../module/shop/model/ShopSuccessResponse";
import { UserProfileSuccessResponse } from "./api-response";
import {
  PartnershipListSuccessResponse,
  PartnershipSuccessResponse,
} from "./api-response/PartnershipListSuccessResponse";
import {
  MemberCardTypeGetSuccessResponse,
  MemberCardTypesSuccessResponse,
} from "../module/member-card-type/models/MemberCardTypesSuccessResponse";
import { MemberCardPoolsSuccessResponse } from "./api-response/MemberCardPoolsSuccessResponse";
import { PromotionCodeSuccessResponse } from "../module/promotion-code/model/PromotionCodeSuccessResponse";
import { GetVoucherTypeListSuccessResponse } from "../module/shop/model/GetVoucherTypeListSuccessResponse";
import { BeaconChargesSuccessResponse } from "./api-response/BeaconChargesSuccessResponse";
import { AdminUserSuccessResponse } from "./api-response/AdminUserSuccessResponse";
import { EmailTemplateGetSuccessResponse } from "../module/email-template/models/EmailTemplatesSuccessResponse";
import {
  HistoricalBalanceSuccessResponse,
  UserBalanceSuccessResponse,
} from "./api-response/UserBalanceSuccessResponse";
import { WithdrawBalance } from "../module/user/model/WithdrawBalance";
import { DepositBalance } from "../module/user/model/DepositBalance";
import { SetRole } from "../module/user/model/SetRole";
import { GetUserVoucherListSuccessResponse } from "../module/shop/model/GetUserVoucherListSuccessResponse";
import { DeleteUserVoucher } from "../module/user/model/DeleteUserVoucher";
import { GetUserVoucherSuccessResponse } from "../module/shop/model/GetUserVoucherSuccessResponse";
import { CreateUserVoucher } from "../module/user/model/CreateUserVoucher";
import { UpdateUser } from "../module/user/model/UpdateUser";
import { AnonymizeUser } from "../module/user/model/AnonymizeUser";
import { UpdatePartnership } from "../module/partnership/model/UpdatePartnership";
import { CreatePartnership } from "../module/partnership/model/CreatePartnership";
import { CreatePromotionCode } from "../module/promotion-code/model/CreatePromotionCode";
import { UpdatePromotionCode } from "../module/promotion-code/model/UpdatePromotionCode";
import { CreateEmailTemplate } from "../module/email-template/models/CreateEmailTemplate";
import { UpdateEmailTemplate } from "../module/email-template/models/UpdateEmailTemplate";
import { UpdateMemberCardType } from "../module/member-card-type/models/UpdateMemberCardType";
import { CreateMemberCardType } from "../module/member-card-type/models/CreateMemberCardType";
import { MemberCardTypesInsertSuccessResponse } from "../module/member-card-type/models/MemberCardTypesInsertSuccessResponse";
import { CreateVoucher } from "../module/voucher-type/models/CreateVoucher";
import { UpdateVoucher } from "../module/voucher-type/models/UpdateVoucher";
import { GetVoucherTypeSuccessResponse } from "../module/shop/model/GetVoucherTypeSuccessResponse";
import { InsertVoucherTypeSuccessResponse } from "../module/voucher-type/models/InsertVoucherTypeSuccessResponse";
import HeapsError from "../heaps-error";
import { UpdateSortingData } from "../model/UpdateSortingData";
import { PromotionCodesSuccessResponse } from "./api-response/PromotionCodesSuccessResponse";
import { DeletePayload, PostPayload } from "../model/http-crud-models";
import { UserTerm, UserTermVersion } from "../module/user/model/user-term";

export const axiosAuthenticated = axios.create({
  baseURL: `${config.apiEndpoint}`,
});

const axiosAuthenticatedNobaseUrl = axios.create({
  baseURL: "",
});

const authInterceptor = (requestConfig: any) => {
  const tokenObjectString = localStorage.getItem(
    "heaps_access_token_with_roles"
  );
  const { token } = JSON.parse(tokenObjectString!);
  if (!requestConfig.headers["Content-Type"]) {
    requestConfig.headers["Content-Type"] = "application/json";
  }
  requestConfig.headers["Accept"] = "application/json";
  requestConfig.headers["X-Heaps-Access-Token"] = token;

  return requestConfig;
};

const errorInterceptor = (error: unknown) => {
  const { errorMessage, errorCode } = extractErrorMessageAndErrorCode(error);
  return Promise.reject(new HeapsError({ errorMessage, errorCode }));
};

axiosAuthenticated.interceptors.request.use(authInterceptor);
axiosAuthenticatedNobaseUrl.interceptors.request.use(authInterceptor);
axiosAuthenticated.interceptors.response.use(
  (response) => response,
  errorInterceptor
);
axiosAuthenticatedNobaseUrl.interceptors.response.use(
  (response) => response,
  errorInterceptor
);

export const httpClient = async <TReturn extends object, TPayload = any>(
  config: AxiosRequestConfig<TPayload>
) => {
  const res = await axiosAuthenticated.request<
    any,
    AxiosResponse<TReturn>,
    typeof config.data
  >(config);
  return res.data;
};

export const httpClientNobaseUrl = async <
  TReturn extends object,
  TPayload = any
>(
  config: AxiosRequestConfig<TPayload>
) => {
  const res = await axiosAuthenticatedNobaseUrl.request<
    any,
    AxiosResponse<TReturn>,
    typeof config.data
  >(config);
  return res.data;
};

export const uploadImageAndGetCdnUrl = async ({
  file,
  id,
  organizationKeyName,
  apiRoute,
  type,
  preExtType,
}: BaseUploadImage): Promise<string> => {
  const fileExtension = file.name.split(".").pop() as string;
  const ext = fileExtension.toLowerCase();

  let path = `/organizations/${organizationKeyName}/upload/${apiRoute}${
    !!id ? `/${id}` : ""
  }`;

  if (!!preExtType) {
    path += `/${preExtType}`;
  }

  if (ext !== "pdf") {
    path += `/${ext}`;
  }

  if (!!type) {
    path += `/${type}`;
  }

  const uploadData = await httpClient<ImageUpload>({
    method: "GET",
    url: path,
  });

  let contentType = "";
  if (ext === "jpg" || ext === "jpeg" || ext === "png") {
    contentType = `image/${ext}`;
  } else if (ext === "mp4") {
    contentType = "video/mp4";
  } else if (ext === "mov") {
    contentType = "video/quicktime";
  } else if (ext === "pdf") {
    contentType = "application/pdf";
  }
  if (!contentType) {
    throw new HeapsError({
      errorMessage: `Invalid file type: ${ext}`,
      errorCode: "invalid_file_type",
    });
  }
  await httpClient({
    method: "PUT",
    headers: {
      "Content-Type": contentType,
    },
    url: uploadData.link,
    data: file,
  });

  return uploadData.cdn;
};

export const updateSorting = async ({
  organizationKeyName,
  data,
  apiRoute,
}: UpdateSortingData): Promise<any> => {
  const url = `/organizations/${organizationKeyName}/${apiRoute}/update-order`;
  const res = await httpClient({
    method: "PUT",
    url,
    data,
  });

  return res;
};

// GRADUALLY MOVE BELOW TO MODULES

export const http = {
  getPunchCards: async (organizationKeyName: OrganizationKeyName) => {
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<PunchCardsResponse>
    >(`/organizations/${organizationKeyName}/product-packs`);

    return response.data.data;
  },

  getPunchCard: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    if (!id) return;

    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<PunchCardResponse>
    >(`/organizations/${organizationKeyName}/product-packs/${id}`);
    return response.data.data;
  },

  createPunchCard: async (punchCard: PunchCardType) => {
    const { organizationKeyName, ...rest } = punchCard;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      `/organizations/${organizationKeyName}/product-packs`,
      rest
    );

    return response.data;
  },

  updatePunchCard: async (punchCard: PunchCardType) => {
    const { id, organizationKeyName, ...body } = punchCard;
    const response = await axiosAuthenticated.put(
      `/organizations/${organizationKeyName}/product-packs/${id}`,
      body
    );

    return response.data;
  },

  getAutoAddedProducts: async (
    organizationKeyName: OrganizationKeyName,
    shopId?: string
  ) => {
    if (!shopId) {
      return undefined;
    }

    const res = await httpClient<AutoAddedProductsResponse>({
      method: "GET",
      url: `/organizations/${organizationKeyName}/shops/${shopId}/auto-added-products`,
    });

    return res.data;
  },

  deleteAutoAddedProduct: async ({
    organizationKeyName,
    data: { shopId, id },
  }: DeletePayload<{ shopId: string }>) => {
    return httpClient<{}>({
      method: "DELETE",
      url: `/organizations/${organizationKeyName}/shops/${shopId}/auto-added-products/${id}`,
    });
  },

  createAutoAddedProduct: async ({
    data: { shopId, ...rest },
    organizationKeyName,
  }: PostPayload<AutoAddedProductMutationPayload & { shopId: string }>) => {
    return httpClient<{ id: string }>({
      method: "PUT",
      url: `/organizations/${organizationKeyName}/shops/${shopId}/auto-added-products`,
      data: rest,
    });
  },

  archiveVoucherType: async ({
    id,
    organizationKeyName,
  }: ArchiveVoucherTypeMutationType) => {
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      `/organizations/${organizationKeyName}/voucher-types/items/${id}/archive`
    );

    return response.data;
  },

  getChallenge: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    if (!id) return;

    const challenges = await axiosAuthenticated.get<
      any,
      AxiosResponse<ChallengesResponse>
    >(`/organizations/${organizationKeyName}/challenges`);
    const challenge = challenges.data.challenges.find(
      (challenge) => challenge.id === id
    );
    return challenge;
  },

  getChallenges: async (organizationKeyName: OrganizationKeyName) => {
    const challenges = await axiosAuthenticated.get<
      any,
      AxiosResponse<ChallengesResponse>
    >(`/organizations/${organizationKeyName}/challenges`);
    return challenges.data.challenges;
  },

  updateChallenge: async (challenge: ChallengeType) => {
    const { id, organizationKeyName, ...body } = challenge;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      `/organizations/${organizationKeyName}/challenges/${id}`,
      body
    );

    return response.data;
  },

  deleteChallenge: async ({ organizationKeyName, id }: DeleteChallengeType) => {
    const response = await axiosAuthenticated.delete<any, AxiosResponse>(
      `/organizations/${organizationKeyName}/challenges/${id}`
    );
    return response;
  },

  createChallenge: async (challenge: ChallengeType): Promise<string> => {
    const { organizationKeyName, ...body } = challenge;

    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<{ data: Omit<ChallengeType, "id"> & { id: string } }>
    >(`/organizations/${organizationKeyName}/challenges`, body);

    return response.data.data.id;
  },

  createDeliveryRuleGroup: async (rule: DeliveryRuleForm) => {
    const { organizationKeyName, ...rest } = rule;
    let url = `/organizations/${organizationKeyName}/delivery-rule-groups`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      rest
    );

    return response.data;
  },

  updateDeliveryRuleGroup: async (rule: DeliveryRuleForm) => {
    const { organizationKeyName, id, ...rest } = rule;
    let url = `/organizations/${organizationKeyName}/delivery-rule-groups/${id}`;

    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      rest
    );

    return response.data;
  },

  getDeliveryRuleGroups: async (organizationKeyName: OrganizationKeyName) => {
    let url = `/organizations/${organizationKeyName}/delivery-rule-groups`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<DeliveryRulesSuccessResponse>
    >(url);
    return response.data.data;
  },

  getDeliveryRuleGroup: async (
    organizationKeyName: OrganizationKeyName,
    groupId: string | undefined
  ) => {
    let url = `/organizations/${organizationKeyName}/delivery-rule-groups`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<DeliveryRulesSuccessResponse>
    >(url);
    const deliveryRule = response.data.data.find((rule) => rule.id === groupId);
    return deliveryRule;
  },

  getUploadImageLink: async (url: string) => {
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<ImageUpload>
    >(url);
    return response.data;
  },

  uploadImage: async ({ file, id, organizationKeyName }: UploadImage) => {
    return uploadImageAndGetCdnUrl({
      file,
      id,
      organizationKeyName,
      apiRoute: "challenge",
    });
  },

  getUserChallenges: async (
    organizationKeyName: OrganizationKeyName,
    userId: string | undefined
  ) => {
    let url = `/organizations/${organizationKeyName}/users/${userId}/challenges `;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<UserChallengeApiResponse>
    >(url);
    return response.data.challenges;
  },

  getOrganizationUserTerms: async (
    organizationKeyName?: OrganizationKeyName
  ) => {
    const url = `/organizations/${organizationKeyName}/user-term`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<UserTermListSuccessResponse>
    >(url);
    return response.data.data;
  },

  uploadHomepageImage: async ({
    file,
    id,
    organizationKeyName,
  }: UploadImage) => {
    return uploadImageAndGetCdnUrl({
      file,
      id,
      organizationKeyName,
      apiRoute: "homepage-config",
    });
  },

  getHomepages: async (organizationKeyName: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/homepage-config`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<HomepageConfigListSuccessResponse>
    >(url);
    return response.data.data;
  },

  getHomepage: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/homepage-config`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<HomepageConfigListSuccessResponse>
    >(url);
    const homepages = response.data.data;
    const homepage = homepages.find((homepage) => homepage.id === id);
    return homepage;
  },

  createHomepage: async ({ organizationKeyName, data }: CreateHomepage) => {
    const url = `/organizations/${organizationKeyName}/homepage-config`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<{ id: string }>
    >(url, data);
    return response.data.id;
  },

  updateHomepage: async ({ organizationKeyName, id, data }: UpdateHomepage) => {
    const url = `/organizations/${organizationKeyName}/homepage-config/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );
    return response.data.data;
  },

  getNewsList: async (
    organizationKeyName: OrganizationKeyName,
    page: number = 1
  ) => {
    const url = `/organizations/${organizationKeyName}/announcement?page=${page}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<NewsListSuccessResponse>
    >(url);
    return response.data.data;
  },

  getNews: async (organizationKeyName: OrganizationKeyName, id?: string) => {
    const url = `/organizations/${organizationKeyName}/announcement/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<NewsSuccessResponse>
    >(url);
    return response.data.data;
  },

  createNews: async ({ organizationKeyName, data }: CreateNews) => {
    const url = `/organizations/${organizationKeyName}/announcement`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<{ id: string }>
    >(url, data);
    return response.data.id;
  },

  updateNews: async ({ organizationKeyName, id, data }: UpdateNews) => {
    const url = `/organizations/${organizationKeyName}/announcement/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );
    return response.data.data;
  },

  deleteNews: async ({ organizationKeyName, data: { id } }: DeletePayload) => {
    return httpClient<{}>({
      method: "DELETE",
      url: `/organizations/${organizationKeyName}/announcement/${id}`,
    });
  },

  uploadNewsImage: async ({ file, id, organizationKeyName }: UploadImage) => {
    return uploadImageAndGetCdnUrl({
      file,
      id,
      organizationKeyName,
      apiRoute: "announcement",
    });
  },

  getCountries: async () => {
    const url = "/countries";
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<CountriesSuccessResponse>
    >(url);
    return response.data.countries.items;
  },

  getAppCallToActions: async (organizationKeyName: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/ctas`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<AppCallToActionsSuccessResponse>
    >(url);
    return response.data.ctas;
  },

  getAppCallToAction: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/ctas`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<AppCallToActionsSuccessResponse>
    >(url);
    const ctas = response.data.ctas;
    const cta = ctas.find((cta) => cta.id === id);
    return cta;
  },

  createCta: async ({ organizationKeyName, data }: CreateCallToAction) => {
    const url = `/organizations/${organizationKeyName}/ctas`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<{ id: string }>
    >(url, data);
    return response.data.id;
  },

  updateCta: async ({ organizationKeyName, data, id }: UpdateCallToAction) => {
    const url = `/organizations/${organizationKeyName}/ctas/${id}`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      data
    );
    return response.data;
  },

  deleteCta: async ({ organizationKeyName, data: { id } }: DeletePayload) => {
    const url = `/organizations/${organizationKeyName}/ctas/${id}`;
    const response = await axiosAuthenticated.delete<any, AxiosResponse>(url);
    return response.data;
  },

  getUserOrders: async (
    userId: string,
    organizationKeyName: OrganizationKeyName
  ) => {
    const url = `/organizations/${organizationKeyName}/users/${userId}/orders`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<UserOrdersSuccessResponse>
    >(url);
    return response.data.data;
  },

  getAdditionGroups: async (organizationKeyName: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/shop-product-additions`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<ShopProductAdditionsListSuccessResponse>
    >(url);
    return response.data.data;
  },

  getAdditionGroup: async (
    organizationKeyName: OrganizationKeyName,
    id: string | undefined
  ) => {
    const url = `/organizations/${organizationKeyName}/shop-product-additions/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<AdditionGroupSuccesResponse>
    >(url);
    return response.data.data;
  },

  createAdditionGroup: async ({
    organizationKeyName,
    data,
  }: AdditionGroupPayloadCreation) => {
    const url = `/organizations/${organizationKeyName}/shop-product-additions`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  updateAdditionGroup: async ({
    organizationKeyName,
    data,
    id,
  }: AdditionGroupPayloadUpdate) => {
    const url = `/organizations/${organizationKeyName}/shop-product-additions/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  archiveAdditionGroup: async ({
    id,
    organizationKeyName,
  }: ArchiveAdditionGroup) => {
    const url = `/organizations/${organizationKeyName}/shop-product-additions/${id}/archive`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url);

    return response;
  },

  getUserMembercards: async (
    organizationKeyName: OrganizationKeyName,
    userId?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/users/${userId}/member-cards`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<UsersMemberCardsSuccessResponse>
    >(url);
    const cards = response.data.memberCards.items;
    return cards;
  },

  checkUserMembercardStatus: async (
    organizationKeyName: OrganizationKeyName,
    cardId: string
  ) => {
    const url = `/organizations/${organizationKeyName}/cards/${cardId}/pos-status`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<UserMemberCardPosStatusSuccessResponse>
    >(url);
    const cardData = response.data.posCardData;
    return cardData;
  },

  syncUserMembercardStatus: async ({
    organizationKeyName,
    cardId,
  }: UserMemberCardSyncPayload) => {
    const url = `/organizations/${organizationKeyName}/cards/${cardId}/sync`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<UserMemberCardSyncCardSuccessResponse>
    >(url);

    return response.data.status;
  },

  createUserMembercard: async ({
    organizationKeyName,
    userId,
    data,
  }: UserMemberCardCreatePayload) => {
    const url = `/organizations/${organizationKeyName}/users/${userId}/member-cards/create-by-partnership`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<UserMemberCardSyncCardSuccessResponse>
    >(url, data);

    return response.data.status;
  },

  deleteUserMembercard: async ({
    organizationKeyName,
    cardId,
  }: UserMemberCardDeletePayload) => {
    const url = `/organizations/${organizationKeyName}/member-cards/${cardId}`;
    const response = await axiosAuthenticated.delete<
      any,
      AxiosResponse<UserMemberCardSyncCardSuccessResponse>
    >(url);

    return response.data.status;
  },

  getShops: async (organizationKeyName: string) => {
    const url = `/organizations/${organizationKeyName}/shops`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<ShopListSuccessResponse>
    >(url);
    const shops = response.data.data;
    return shops;
  },

  getShop: async (organizationKeyName: string, id: string) => {
    const url = `/organizations/${organizationKeyName}/shops/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<ShopSuccessResponse>
    >(url);
    const shop = response.data.data;
    return shop;
  },

  getUserProfile: async (organizationKeyName: string, id?: string | number) => {
    const url = `/organizations/${organizationKeyName}/users/${id}/profile`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<UserProfileSuccessResponse>
    >(url);
    const userProfile = response.data;
    return userProfile;
  },

  getPartnership: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/partnerships/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<PartnershipSuccessResponse>
    >(url);
    const partnership = response.data.data;
    return partnership;
  },

  createPartnership: async ({
    organizationKeyName,
    data,
  }: CreatePartnership) => {
    const url = `/organizations/${organizationKeyName}/partnerships`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  updatePartnership: async ({
    data,
    id,
    organizationKeyName,
  }: UpdatePartnership) => {
    const url = `/organizations/${organizationKeyName}/partnerships/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  getMembercardPools: async (organizationKeyName: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/member-card-pools`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MemberCardPoolsSuccessResponse>
    >(url);
    const membercardPools = response.data.member_card_pools;
    return membercardPools;
  },
  getUserProfileBalances: async (
    organizationKeyName: string,
    id?: string | number
  ) => {
    const url = `/organizations/${organizationKeyName}/users/${id}/account/balance`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<UserBalanceSuccessResponse | null>
    >(url);
    const balance = response.data?.balances;
    return balance || null;
  },

  getUserProfileHistoricalBalances: async (
    organizationKeyName: string,
    countryCode: string,
    id?: string | number
  ) => {
    const url = `/organizations/${organizationKeyName}/users/${id}/account/${countryCode}/balance/top-up-history`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<HistoricalBalanceSuccessResponse>
    >(url);
    const balances = response.data?.data;
    return balances || [];
  },

  getUserVouchers: async (
    organizationKeyName: string,
    filters: any,
    id?: string | number
  ) => {
    const filtersStr = queryString.stringify(filters || {}, {
      arrayFormat: "bracket",
    });

    const url = `/organizations/${organizationKeyName}/users/${id}/vouchers?${filtersStr}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<GetUserVoucherListSuccessResponse>
    >(url);
    const vouchers = response.data.vouchers;
    return vouchers;
  },

  deleteUserVoucher: async ({ organizationKeyName, id }: DeleteUserVoucher) => {
    const url = `/organizations/${organizationKeyName}/vouchers/${id}`;
    const response = await axiosAuthenticated.delete<any, AxiosResponse>(url);

    return response.data;
  },

  createUserVoucher: async ({
    organizationKeyName,
    data,
  }: CreateUserVoucher) => {
    const url = `/organizations/${organizationKeyName}/vouchers`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url, {
      ...data,
    });

    return response;
  },

  getUserVoucher: async (organizationKeyName?: string, id?: string) => {
    const url = `/organizations/${organizationKeyName}/vouchers/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<GetUserVoucherSuccessResponse>
    >(url);
    const voucher = response.data.data;
    return voucher;
  },

  withdrawUserBalance: async ({
    organizationKeyName,
    userId,
    countryCode,
  }: WithdrawBalance) => {
    const url = `/organizations/${organizationKeyName}/users/${userId}/account/deposit-balance/withdraw`;
    const params: any = {
      refund: true,
    };

    if (countryCode) {
      params.countryCode = countryCode;
    }
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      params
    );

    return response;
  },

  depositUserBalance: async ({
    organizationKeyName,
    userId,
    countryCode,
    amount,
  }: DepositBalance) => {
    const url = `/organizations/${organizationKeyName}/users/${userId}/account/deposit`;
    const params: any = {
      amount: amount,
      countryCode: countryCode,
    };
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      params
    );

    return response;
  },

  setUserRoles: async ({ data, organizationKeyName, userId }: SetRole) => {
    const url = `/organizations/${organizationKeyName}/users/${userId}/roles`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url, {
      roles: data,
    });

    return response;
  },

  getPartnerships: async (organizationKeyName: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/partnerships`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<PartnershipListSuccessResponse>
    >(url);
    const partnerships = response.data.data;
    return partnerships;
  },

  getVouchers: async (organizationKeyName: string) => {
    const url = `/organizations/${organizationKeyName}/voucher-types/items`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<GetVoucherTypeListSuccessResponse>
    >(url);
    const vouchers = response.data.voucher_types;
    return vouchers;
  },

  getVoucher: async (organizationKeyName: string, id?: string) => {
    const url = `/organizations/${organizationKeyName}/voucher-types/items/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<GetVoucherTypeSuccessResponse>
    >(url);
    const voucher = response.data.data;
    return voucher;
  },

  createVoucher: async ({ data, organizationKeyName }: CreateVoucher) => {
    const url = `/organizations/${organizationKeyName}/voucher-types/items`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<InsertVoucherTypeSuccessResponse>
    >(url, data);

    return response.data.data;
  },

  updateVoucher: async ({ data, id, organizationKeyName }: UpdateVoucher) => {
    const url = `/organizations/${organizationKeyName}/voucher-types/items/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  uploadVoucherTypeImage: async ({
    file,
    id,
    organizationKeyName,
  }: UploadImage) => {
    return uploadImageAndGetCdnUrl({
      file,
      id,
      organizationKeyName,
      apiRoute: "voucher-type",
    });
  },

  getPromotionCode: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/promotion-code/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<PromotionCodeSuccessResponse>
    >(url);
    const code = response.data.data;
    return code;
  },

  getPromotionCodes: async (
    organizationKeyName: OrganizationKeyName,
    filters: { [i: string]: string }
  ) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      params.set(key, value);
    });
    const url = `/organizations/${organizationKeyName}/promotion-code?${params.toString()}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<PromotionCodesSuccessResponse>
    >(url);
    const code = response.data.promotionCodes.items;
    return code;
  },

  getBeaconCharges: async (
    organizationKeyName: OrganizationKeyName,
    idNfc: string | undefined,
    filters: { [i: string]: any },
    id?: string
  ) => {
    const params = {
      ...(filters || {}),
    };

    if (idNfc) {
      params.filterByBeaconIdNfc = idNfc;
    }

    const filtersStr = "?" + queryString.stringify(params);
    const url = `/organizations/${organizationKeyName}/beacon/${id}/charges${filtersStr}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<BeaconChargesSuccessResponse>
    >(url);
    const charges = response.data.drinksPayCharge;
    return charges;
  },

  getEmailTemplate: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/email-templates/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<EmailTemplateGetSuccessResponse>
    >(url);
    const emailTemplate = response.data.data;
    return emailTemplate;
  },

  getAdminUser: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/security/admin-users/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<AdminUserSuccessResponse>
    >(url);
    const admin = response.data.data;
    return admin;
  },

  createEmailTemplate: async ({
    data,
    organizationKeyName,
  }: CreateEmailTemplate) => {
    const url = `/organizations/${organizationKeyName}/email-templates`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  updateEmailTemplate: async ({
    data,
    id,
    organizationKeyName,
  }: UpdateEmailTemplate) => {
    const url = `/organizations/${organizationKeyName}/email-templates/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  createPromotionCode: async ({
    data,
    organizationKeyName,
  }: CreatePromotionCode) => {
    const url = `/organizations/${organizationKeyName}/promotion-code`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  updatePromotionCode: async ({
    data,
    id,
    organizationKeyName,
  }: UpdatePromotionCode) => {
    const url = `/organizations/${organizationKeyName}/promotion-code/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  updateUser: async ({ data, organizationKeyName, userId }: UpdateUser) => {
    const url = `/organizations/${organizationKeyName}/users/${userId}/update`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  updateUserTermConsent: async ({
    termId,
    status,
    organizationKeyName,
    userId,
  }: {
    organizationKeyName: string;
    userId: string;
    termId: string;
    status: "0" | "1"; // 0: not accepted, 1: accepted
  }) => {
    const url = `/organizations/${organizationKeyName}/users/${userId}/consent/${termId}/${status}`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url);

    return response;
  },

  getUserTerms: async ({
    organizationKeyName,
  }: {
    organizationKeyName: OrganizationKeyName;
  }) => {
    const url = `/organizations/${organizationKeyName}/user-term`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<{ data: UserTerm[] }>
    >(url);

    return response.data.data;
  },

  getUserTermVersions: async ({
    organizationKeyName,
    termId,
  }: {
    organizationKeyName: OrganizationKeyName;
    termId: string;
  }) => {
    const url = `/organizations/${organizationKeyName}/user-term/${termId}/version`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<{ data: UserTermVersion[] }>
    >(url);

    return response.data.data;
  },

  anynomizeUser: async ({ organizationKeyName, userId }: AnonymizeUser) => {
    const url = `/organizations/${organizationKeyName}/users/${userId}/anonymize`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url, {
      shouldAnonymize: true,
    });

    return response;
  },

  getMembercardTypes: async (organizationKeyName: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/member-card-types`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MemberCardTypesSuccessResponse>
    >(url);
    const membercardTypes = response.data.member_card_types;
    return membercardTypes;
  },

  getMembercardType: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/member-card-types/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MemberCardTypeGetSuccessResponse>
    >(url);
    const membercard = response.data.data;
    return membercard;
  },

  updateMembercardType: async ({
    organizationKeyName,
    id,
    data,
  }: UpdateMemberCardType) => {
    const url = `/organizations/${organizationKeyName}/member-card-types/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  createMembercardType: async ({
    organizationKeyName,
    data,
  }: CreateMemberCardType) => {
    const url = `/organizations/${organizationKeyName}/member-card-types`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<MemberCardTypesInsertSuccessResponse>
    >(url, data);

    return response.data.data;
  },

  uploadMembercardTypeImage: async ({
    file,
    id,
    organizationKeyName,
    type,
  }: UploadImage & { type: "background" | "logo" }) => {
    return uploadImageAndGetCdnUrl({
      file,
      id,
      organizationKeyName,
      apiRoute: "member-card-types",
      type,
    });
  },

  uploadCallToActionImage: async ({
    file,
    id,
    organizationKeyName,
  }: UploadImage) => {
    return uploadImageAndGetCdnUrl({
      file,
      id,
      organizationKeyName,
      apiRoute: "homepage-config-ctas-image",
    });
  },

  updateSorting: async ({
    organizationKeyName,
    data,
    apiRoute,
  }: UpdateSortingData): Promise<string> => {
    const url = `/organizations/${organizationKeyName}/${apiRoute}/update-order`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response.data.id;
  },
};
